import React,{useRef} from 'react'
import { Link, useLocation} from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useEffect, useState } from 'react'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Popover from 'react-bootstrap/Popover'
import Tooltip from 'react-bootstrap/Tooltip'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Form from 'react-bootstrap/Form'
import EventStatusOrbs from '../../EventStatusOrbs/EventStatusOrbs'
import Routine from '../Routine/Routine'
import Foods from '../Foods/Foods'
import NutritionGoals from '../NutritionGoals/NutritionGoals'
import SimplifiedFoods from '../SimplifiedFoods/SimplifiedFoods'
import {
	updateCopy,
	addEventNote,
	openComments,
	setShowDetails,
	getEventDetails,
	getEventTypeKey,
	toggleDeleteItem,
	setResultsTracking,
	addEventToFavorites,
	updateEvent,
	removeEventSeries,
	updateNotesEditor
} from '../../../state/events'
import {
	selectUserById,
	selectTrainerParam
} from '../../users/usersSlice'
import DiscussResourceButton from '../../DiscussResourceButton/DiscussResourceButton'
import { useEvent } from '../../../hooks/events'
import usePermissions from '../../../hooks/usePermissions'
import { capitalize, envAwarePost, isValidHttpUrl } from '../../../util/util'
import { useDebounceCallback } from '@react-hook/debounce'
import PendingSuccessButton from '../../PendingSuccessButton/PendingSuccessButton'
import useApiStatusDispatch from "../../../hooks/useApiStatusDispatch"
import useCurrentUser from '../../../hooks/useCurrentUser'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as faIcons  from '@fortawesome/free-solid-svg-icons'

const SubItems = ({ filter, event, eventType, trackingResults, toggleResultsTracking }) => {
	const dispatch = useDispatch()
	//show a spinner if we don't have details for workout/nutrition
	//tasks don't have details
	if (!event.hasDetails && eventType !== 'task') {
		return <Spinner animation="border" />
	}

	switch (eventType) {
		case "workout": return (
			<Routine
				workout_id={event.workout_id}
				title={event.note}
				filter={filter}
				eventType={eventType}
				routine={event.routine}
				client_id={event.client_id}
				trackingResults={trackingResults}
				isPlayable={event.is_playable === 1}
				compactMode={Boolean(event.compact_mode)}
				toggleResultsTracking={toggleResultsTracking}
			/>
		)
		case "task": return <div></div>
		case "nutrition": return (
			event.simplified === 0 ?
				<Foods 
					foods={event.food} 
					nutrition_id={event.nutrition_id}
					goalMacros={{
						fat:event.fat_goal,
						calories:event.calorie_goal,
						protein:event.protein_goal,
						carbs:event.carb_goal,
						fiber:event.fiber_goal,
						sodium:event.sodium_goal,
					}}
				/>
				:
				<SimplifiedFoods
					nutrition_id={event.nutrition_id}
					foods={event.food}
				/>
		)
		default: return (null)
	}
}

const eventTypeToPrintSelector = type => (
	['workout', 'task'].includes(type) ? `${capitalize(type)}s` : capitalize(type)
)

const eventColor = type => {
	switch(type){
		case 'workout': return "rgb(103, 209, 206)"
		case 'nutrition': return "rgb(255, 156, 33)"
		case 'task': return "rgb(0, 223, 26)"
		case 'note': return "rgb(255, 235, 59)"
	}
}

//workout task nutrition 
const StandardEventRow = ({ id, eventType, filter, firstGif, setFirstGif }) => {
	const dispatch = useDispatch()
	const commentsRef = useRef()
	const statusDispatch = useApiStatusDispatch();

	const favoriteOrGymFilter = ['favorite', 'gym', 'gym_trainer'].includes(filter)

	const event = useEvent(eventType, id)

	const isTrainingSession = Boolean(event?.calendar_id)

	const user = useCurrentUser()
	const isClient = useSelector(state => state.users.current_user.account_type) === "client"
	const isProspect = useSelector(state => state.users.current_user.account_type) === "prospect"
	const userId = useSelector(state => state.users.current_user.user_id_number);
	const permissions = useSelector(state => state.users.current_user.edit_permissions);
	const trainerParam = useSelector(selectTrainerParam)
    const isTime24Hour = Number(useSelector(state => state.users.current_user.is_24_hour_time))

	const trackingResults = Boolean(event?.tracking)
	const showDetails = Boolean(event?.showDetails)
	const [showEventComments,setShowEventComments] = useState(false)

	const dispatchGetEventDetails = () => (
		dispatch(getEventDetails({
			event_type: eventType,
			event_id: event[`${eventType}_id`]
		}))
	)
	const toggleShowDetails = () => {
		//if results tracking is enabled and we are closing
		//then disable tracking
		if (showDetails && trackingResults) {
			dispatch(setResultsTracking({
				type: eventType,
				tracking: false,
				id
			}))
		}
		setShowEventComments(!showDetails)
		//if no event details fetch them for workouts/nutrition
		//tasks do not have details
		if (!event.hasDetails) {
			dispatchGetEventDetails()
		}

		dispatch(setShowDetails({
			type: eventType,
			showDetails: !showDetails,
			id
		}))
	}

	const toggleShowComments = ()=>{
		setShowEventComments(!showEventComments)
		if(commentsRef.current){
			commentsRef.current.scrollIntoView({behavior:'smooth',block:'center'})
			commentsRef.current.focus()
			//hacky way to get the cursor to be correctly focused to the END
			//of the text area
			commentsRef.current.setSelectionRange(5000,4000)
		}
	}

	//converts a timestring AM/PM  
	const getTimeString = (timestring) => {
		const splitTimestring = timestring.split(':')
		const HH = parseInt(splitTimestring[0])
		const m = splitTimestring[1]

		// if settings are for 24 hour time then do not convert to AM/PM
		if (isTime24Hour) return HH + ":" + m; 
		
		//convert to 12 hour time
		const p = HH >= 12 ? 'PM' : 'AM'
		const h = (
			//subtract 12 from all hours greater than 12
			HH > 12 ?
				HH - 12 :
				//if hour is 0 make it 12 AM 
				//else return hour
				(HH === 0 ? 12 : HH)
		)
		let timeStringFormatted = h + ":" + m + p
		return timeStringFormatted;
	}


	function handlePublishEvent(e) {
		if (!window.confirm(event.published ? "Save " + capitalize(eventType) + " as Draft?" : "Publish this " + capitalize(eventType) + "?" )) return
		statusDispatch(updateEvent({
			event_type: eventType,
			event_id: id,
			published: event.published ? "0" : "1"
		}), `${eventType}`, "save")
		.then(res=>{
			dispatchGetEventDetails()
		})
		e.currentTarget.blur();
	}

	const toggleResultsTracking = () => {
		//if not showing details and enabling
		//results tracking expand routine
		if (!showDetails && !trackingResults) {
			dispatch(setShowDetails({
				type: eventType,
				showDetails: true,
				id
			}))
		}

		const dispatchSetResultsTracking = () => {
			dispatch(setResultsTracking({
				type: eventType,
				tracking: !trackingResults,
				id
			}))
		}

		dispatchSetResultsTracking();

		if (eventType === 'task') return
		//if no event details fetch them for workouts/nutrition
		//then dispatch set results tracking. Else, immediatly set
		//results tracking
		if (!event.hasDetails) {
			dispatchGetEventDetails().then(() => {
				dispatchSetResultsTracking()
			})
		} else {
			dispatchSetResultsTracking()
		}
	}

	const handleDeleteSeries = ()=>{
		if(!window.confirm(`Are you sure you want to delete this ${eventType} series?`)) return
		dispatch(removeEventSeries({
			event_type:eventType,
			recurring_id:event.recurring
		}))
	}

	//get client 
	const client = useSelector(state => selectUserById(state, event?.client_id) || {})

	const { toDelete } = useSelector(state => state.allEvents)
	const debouncedAddEventNote = useDebounceCallback( (note) => {
		statusDispatch(
			addEventNote({
				event_id: id,
				event_type: eventType,
				note
			}),
			'event note',
			'edit',
			`Your ${isClient?'trainer(s)':'client'} will be notified`
		)
	}, 400)

	const [showMobileOptions,setShowMobileOptions] = useState(false)
	const Options = ()=>{
		return (
			<div onClick={e=>{e.stopPropagation()}}>
				<ButtonGroup size="sm" className>
					{((!isClient || permissions[eventType] == "1") && !isProspect && !['gym', 'gym_trainer'].includes(filter)) &&
						<OverlayTrigger overlay={
							<Tooltip>
								Edit {capitalize(eventType)}
							</Tooltip>
						}>
							<Button
								as={Link}
								to={`/eventEditor/?event_id=${id}&event_type=AddOrEdit&eventType=${capitalize(eventType)}&client=${filter === 'favorite' ? "favorite" : event.client_id}&${trainerParam}`}
								variant="outline-primary"
							>
								<i className="fa fa-edit" />
							</Button>
						</OverlayTrigger>
					}
					{(!isClient || permissions[eventType] == "1") &&
						<OverlayTrigger overlay={
							<Tooltip>
								Copy {capitalize(eventType)}
							</Tooltip>
						}>
							<Button
								onClick={(e) => {
									setShowMobileOptions(false)
									dispatch(updateCopy({
										show: true,
										type: eventType,
										id,
										defaultClientSelected: client.user_id_number

									}))
									e.currentTarget.blur();
								}}
								variant="outline-primary"
							>
								<i className="fas fa-copy" />
							</Button>
						</OverlayTrigger>
					}
					{!isClient && !favoriteOrGymFilter &&
						<OverlayTrigger overlay={
							<Tooltip>
								Email {capitalize(eventType)} to Client
							</Tooltip>
						}>
							<PendingSuccessButton
								onClick={({ onPending, onSuccess, onFailed }) => {
									onPending()
									envAwarePost('emailEvent/', {
										//tell api endpoint to send a response
										//rather than issuing a redirect
										use_json_response: 1,
										eventType: eventType,
										client_id: event.client_id,
										event_id: id
									}).then(({ success, message }) => {
										if (success === '1') {
											onSuccess()
										} else {
											onFailed()
											window.alert(message)
										}

									})
								}}
								variant="outline-primary"
							>
								<i className="fas fa-at" />
							</PendingSuccessButton>
						</OverlayTrigger>
					}
					<OverlayTrigger overlay={
						<Tooltip>
							Print {capitalize(eventType)}
						</Tooltip>
					}>
						<Button
							href={`/printEvents/?display=${id}&selector=${eventTypeToPrintSelector(eventType)}&client=${event.client_id}`}
							variant="outline-primary"
							target="_blank"
							rel="noreferrer noopener"
						>
							<i className="fa fa-print" />
						</Button>
					</OverlayTrigger>
					{(!favoriteOrGymFilter || isClient) &&
						<>
							<OverlayTrigger overlay={
								<Tooltip>
									{capitalize(eventType)} Notes
									{event.unseen_notes>0&&
										<>
											<br/>
											<small>New</small>
										</>
									}
								</Tooltip>
							}>
								<Button
									onClick={toggleShowComments}
									variant={
										event.unseen_notes>0
										?"primary"
										:"outline-primary"
									}
								>
									<i className="fa fa-comment" />
								</Button>
							</OverlayTrigger>
							{!isClient && <OverlayTrigger overlay={
								<Tooltip>
									Copy {capitalize(eventType)} to Favorites
								</Tooltip>
							}>
								<Button
									onClick={(e) => {
										if (!window.confirm('Copy event to favorites?')) return
										// dispatch(addEventToFavorites({
										// 	event_type: eventType,
										// 	event_id: id
										// }))
										statusDispatch(addEventToFavorites({
											event_type: eventType,
											event_id: id,
										}), `${eventType}`, "favorite")
										e.currentTarget.blur();

									}}
									variant="outline-primary"
								>
									<i className="fa fa-star" />
								</Button>
							</OverlayTrigger>}
						</>
					}
					{!isClient && !favoriteOrGymFilter && <OverlayTrigger overlay={
						<Tooltip>
							 {event.published === 0 ? `Publish ${capitalize(eventType)}` : `Save ${capitalize(eventType)} as Draft`}
						</Tooltip>
					}>
						<Button
							onClick={(e) => {
								handlePublishEvent(e)

							}}
							variant="outline-primary"
						>
							 {event.published === 0 ? <i class="far fa-bookmark"></i> : <i class="fas fa-bookmark"></i>}
						</Button>
					</OverlayTrigger>}
				</ButtonGroup>
			</div>
		)
	}
	
	if (!event) return null
	return (
		<>
			<tr className="bg-white">
				<td>
					<div
						className="d-flex"
						onClick={toggleShowDetails}
						style={{ cursor: 'pointer' }}
					>
						{Boolean(event.thumbnail) &&
							<OverlayTrigger overlay={
								<Tooltip>
									<img style={{ width: '140px' }} src={event.feature_media} />
								</Tooltip>
							}>
								<img
									style={{ objectFit: 'cover', width: '40px', height: '40px', borderRadius: '8px' }}
									src={event.thumbnail}
									className="mr-1"
								/>
							</OverlayTrigger>
						}
						<div>
							{(!favoriteOrGymFilter || isClient) &&
								<div>
									{event.date && event.date !== "0000-00-00" ?
										<>
											<span> {new Date(event.date.replace(/-/g, '\/')).toDateString()} </span>
											<span> {(event.start_time && event.end_time) && (`(${getTimeString(event.start_time)} - ${getTimeString(event.end_time)})`)}</span>
										</>
										: null}
								</div>
							}
							<div>
								<strong>
									<span className="text-primary mr-1">
										<i 
											style={{
												color:eventColor(event.event_type)
											}}
											className="fas fa-circle mr-1" 
										/>
										{event.note}
									</span>
									{(event.published === 0 && !favoriteOrGymFilter) &&
										<small className="text-danger">
											(Draft {capitalize(eventType)})
										</small>
									}
									{(event.published !== 0 && !favoriteOrGymFilter) &&
										<small className="text-info">
											({capitalize(eventType)})
										</small>
									}
								</strong>
							</div>
						</div>
					</div>
					{Boolean(event.part_of_program)&&
						<div>
							<small>
								Part of program: 
								<Link 
									to={`/program-editor/?id=${event.program.programID}`}
									className="ml-2"
									target="_blank"
								>
									{event.program.programName}
								</Link>
							</small>
						</div>
					}
					{Boolean(event.file)&&
						<div>
							<small>
								File/PDF: 
								<a 
									href={event.file}
									className="ml-2"
									target="_blank"
								>
									View file/PDF
								</a>
							</small>
						</div>
					}
					{Boolean(event.recurring)&&
						<div>
							<small>
								<span className="mr-2">Part of recurring {eventType} series:</span>
								<button 
									onClick={handleDeleteSeries}
									className="text-danger border-0 bg-white" 
									size="sm"
								>
									delete series
								</button>
							</small>
						</div>
					}
					{(Boolean(event.link) && !['http://', 'https://'].includes(event.link)) && isValidHttpUrl(event.link) &&
						<div>
							<a href={event.link}>link</a>
						</div>
					}
				</td>
				{filter === 'all' &&
					<td>
						{/* {client.user_name} */}
						{event.client_user_name}
					</td>
				}
				<td align="middle">
					<div className="d-flex justify-content-between flex-wrap">
						{isTrainingSession&&
							<small>
								<i className="fa fa-info-circle mr-1"/>
								This is a training session to edit/delete please use the <Link to={`/scheduler?id=${event.calendar_id}`}>Scheduler</Link>
							</small>
						}
						{!isTrainingSession&& 
							<div className="d-none d-md-block">
								<Options/>
							</div>	
						}
						{(!favoriteOrGymFilter || isClient) &&
							<div className="ml-auto">
								<EventStatusOrbs
									complete={event.complete}
									event_type={eventType}
									event_id={id}
									setFirstGif={setFirstGif}
									firstGif={firstGif}
								/>
							</div>
						}
						{!isTrainingSession&& 
							<div className="d-block d-md-none">
								<OverlayTrigger show={showMobileOptions} overlay={
									<Popover>
										<Popover.Content>
											<Options/>
										</Popover.Content>
									</Popover>
								}>
									<Button 
										onClick={setShowMobileOptions.bind(null,p=>!p)}
										variant={showMobileOptions?"outline-danger":"outline-primary"}
									>
										<FontAwesomeIcon icon={faIcons[showMobileOptions?'faTimes':'faBars']} className="mr-1"/>
										Options
									</Button>
								</OverlayTrigger>
							</div>
						}
					</div>
				</td>
				<td>
					{(!['gym', 'gym_trainer'].includes(filter)&&!isTrainingSession) &&
						<Form.Check
							onChange={() => {
								dispatch(toggleDeleteItem({itemType:eventType,id}))
							}}
							checked={toDelete[eventType].includes(id)}
							type="checkbox"
							aria-label="check all"
						/>
					}
				</td>
			</tr>
			{showDetails &&
				<tr className="bg-white">
					<td colSpan="100%">
						{(eventType === 'workout' && event?.compliance?.total>0)&&
							<div className="text-info mb-2">
								<strong>Compliance:</strong> {(event.compliance.percent).toFixed(0)}% ({event.compliance.completed}/{event.compliance.total}) of {event.compliance.unit}s completed
							</div>
						}
						{eventType === 'nutrition' && <NutritionGoals nutrition={event} />}
						{Boolean(event.long_note) &&
							<div className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
								<div>General Notes: </div>
								{event.long_note}
							</div>
						}
						<SubItems 
							toggleResultsTracking={toggleResultsTracking}
							trackingResults={trackingResults} 
							eventType={eventType} 
							filter={filter} 
							event={event} 
						/>

					</td>
				</tr>
			}
			{(showDetails || showEventComments)&&
				<tr>
					<td colSpan="100%">
						{!favoriteOrGymFilter &&
							<>
								<DiscussResourceButton 
									className="btn btn-outline-primary btn-block btn-sm mb-1"
									id={event[`${eventType}_id`]}
									user_id_number={isClient?user.user_trainer_id:event.client_id}
									type={eventType}
								>
									Discuss this {eventType} in the chat <i className="fa fa-comments"/>
								</DiscussResourceButton>
								<small className="text-center text-muted d-block">
									Or Add Some Notes Below
								</small>
								<Form.Control
									ref={commentsRef}
									defaultValue={event.client_note}
									placeholder="General notes"
									onChange={ e => {
										debouncedAddEventNote(e.target.value)
									}} 
									as="textarea"
								/>
							</>
						}
					</td>
				</tr>
			}
		</>
	)
}

const NoteRow = ({ id, eventType, filter, firstGif, setFirstGif }) => {
	const dispatch = useDispatch()
	const commentsRef = useRef()
	const statusDispatch = useApiStatusDispatch();

	const favoriteOrGymFilter = ['favorite', 'gym', 'gym_trainer'].includes(filter)

	const note = useSelector(state=>state.clientNotes.adapter.entities[id])
	const user = useCurrentUser()
	const isClient = useSelector(state => state.users.current_user.account_type) === "client"
	let userId = useSelector(state => state.users.current_user.user_id_number);
	const permissions = useSelector(state => state.users.current_user.edit_permissions);
	const trainerParam = useSelector(selectTrainerParam)
    const isTime24Hour = Number(useSelector(state => state.users.current_user.is_24_hour_time))

	//converts a timestring AM/PM  
	const getTimeString = (timestring) => {
		const splitTimestring = timestring.split(':')
		const HH = parseInt(splitTimestring[0])
		const m = splitTimestring[1]

		// if settings are for 24 hour time then do not convert to AM/PM
		if (isTime24Hour) return HH + ":" + m; 
		
		//convert to 12 hour time
		const p = HH >= 12 ? 'PM' : 'AM'
		const h = (
			//subtract 12 from all hours greater than 12
			HH > 12 ?
				HH - 12 :
				//if hour is 0 make it 12 AM 
				//else return hour
				(HH === 0 ? 12 : HH)
		)
		let timeStringFormatted = h + ":" + m + p
		return timeStringFormatted;
	}

	//get client 
	const client = useSelector(state => selectUserById(state, note?.client_id) || {})

	const { toDelete } = useSelector(state => state.allEvents)
	const debouncedAddEventNote = useDebounceCallback( (note) => {
		statusDispatch(
			addEventNote({
				event_id: id,
				event_type: eventType,
				note
			}),
			'event note',
			'edit',
			`Your ${isClient?'trainer(s)':'client'} will be notified`
		)
	}, 400)
	
	if (!note) return null
	return (
		<>
			<tr className="bg-white">
				<td>
					<div>
						{(!favoriteOrGymFilter || isClient) &&
							<div>
								{note.date && note.date !== "0000-00-00" ?
									<>
										<span> {new Date(note.date.replace(/-/g, '\/')).toDateString()} </span>
										<span> {(note.start_time && note.end_time) && (`(${getTimeString(note.start_time)} - ${getTimeString(note.end_time)})`)}</span>
									</>
									: null}
							</div>
						}
						<div style={{fontSize:'14px'}}>
							<i 
								style={{
									color:eventColor(note.event_type)
								}}
								className="fas fa-circle mr-1" 
							/>
							<strong className="mr-1">
								Note
							</strong>
							<small className="text-muted" style={{fontSize:'12px'}}>
								(Not visible to clients)
							</small>
						</div>
					</div>
				</td>
				<td>
					<ButtonGroup size="sm">
						<OverlayTrigger overlay={
							<Tooltip>
								Edit {capitalize(eventType)}
							</Tooltip>
						}>
							<Button
								onClick={()=>{
									dispatch(
										updateNotesEditor({
											clientId:note.client_id,
											id:note.note_id,
											date:note.date,
											note:note.note,
											show:true
										})
									)
								}}
								variant="outline-primary"
							>
								<i className="fa fa-edit" />
							</Button>
						</OverlayTrigger>
					</ButtonGroup>
				</td>
				<td>
					<Form.Check
						onChange={() => {
							dispatch(toggleDeleteItem({itemType:'note',id}))
						}}
						checked={toDelete.note.includes(id)}
						type="checkbox"
						aria-label="check all"
					/>
				</td>
			</tr>
			<tr>
				<td colSpan="100%">
					{capitalize(note.note)}
				</td>
			</tr>
		</>
	)
}

const EventRow = (props) => {
	switch(props.eventType){
		case 'note': return <NoteRow {...props} />
		default: return <StandardEventRow {...props} />
	}
}
export default EventRow